/**
 * CMS authentication state.
 *
 * The access token is held in module memory (see `services/client.ts`) rather than in
 * `localStorage`. On a page reload we recover the session by exchanging the HttpOnly refresh
 * cookie — so a refresh keeps the user signed in without a token ever being readable by script.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi } from '@/services/api';
import { onUnauthorised, setAccessToken } from '@/services/client';
import type { Capabilities, CurrentUser, RoleKey } from '@/services/types';

interface AuthContextValue {
  user: CurrentUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  signIn: (email: string, password: string) => Promise<CurrentUser>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (capability: keyof Capabilities) => boolean;
  hasRole: (...roles: RoleKey[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'anonymous'>('loading');

  const loadSession = useCallback(async () => {
    try {
      const { access } = await authApi.refresh();
      setAccessToken(access);
      setUser(await authApi.me());
      setStatus('authenticated');
    } catch {
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    // Session recovery only runs inside the CMS. A public visitor has no session to recover, so
    // attempting it on every page view would add a guaranteed-to-fail request to each one.
    if (!window.location.pathname.startsWith('/admin')) {
      setStatus('anonymous');
      return;
    }
    void loadSession();
  }, [loadSession]);

  // A failed refresh anywhere in the app means the session is gone; clear it once, centrally.
  useEffect(
    () =>
      onUnauthorised(() => {
        setAccessToken(null);
        setUser(null);
        setStatus('anonymous');
      }),
    [],
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    setAccessToken(response.access);
    setUser(response.user);
    setStatus('authenticated');
    return response.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    setUser(await authApi.me());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      signIn,
      signOut,
      refreshUser,
      can: (capability) => Boolean(user?.capabilities?.[capability]),
      hasRole: (...roles) => Boolean(user && roles.some((role) => user.roles.includes(role))),
    }),
    [user, status, signIn, signOut, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider.');
  return context;
}
